// ParkConnect's icon set — drawn here rather than pulled from a font or an
// emoji, so every glyph shares one stroke weight, one corner radius and
// `currentColor`. Each takes { size, className, strokeWidth } and inherits its
// colour from the text around it.
//
// House rule for this page: no emoji anywhere in the UI. If a new affordance
// needs a mark, add it here beside the others rather than reaching for a
// character.

const BASE = {
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.7,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
};

function Svg({ size = 20, className, strokeWidth, children, ...rest }) {
  return (
    <svg
      width={size} height={size} viewBox="0 0 24 24"
      className={className} aria-hidden="true" focusable="false"
      {...BASE} {...(strokeWidth ? { strokeWidth } : {})} {...rest}
    >
      {children}
    </svg>
  );
}

/* ── Identity ─────────────────────────────────────────────────────────────── */

// The wordmark's glyph: a parking "P" inside a pin. Filled, not stroked, so it
// holds up at favicon size in the header.
export function MarkIcon({ size = 28, className }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" className={className} aria-hidden="true" focusable="false">
      <path
        d="M12 22s7.5-6.4 7.5-12.2A7.5 7.5 0 0 0 4.5 9.8C4.5 15.6 12 22 12 22Z"
        fill="currentColor"
      />
      <path
        d="M10 14V6.6h2.6a2.4 2.4 0 0 1 0 4.8H10"
        fill="none" stroke="#fff" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"
      />
    </svg>
  );
}

/* ── The two account holders ──────────────────────────────────────────────── */

// Permit holder: the hangtag that swings from a mirror.
export function PermitIcon(p) {
  return (
    <Svg {...p}>
      <path d="M9 3h6a1 1 0 0 1 1 1v3.2a4 4 0 0 1-1.2 2.9l-.8.8V20a1 1 0 0 1-1 1h-2a1 1 0 0 1-1-1v-9.1l-.8-.8A4 4 0 0 1 8 7.2V4a1 1 0 0 1 1-1Z" />
      <path d="M10.4 6.4h3.2" />
      <path d="M11 13.6h2" />
    </Svg>
  );
}

// Parker: a car, seen head-on-ish.
export function CarIcon(p) {
  return (
    <Svg {...p}>
      <path d="M4.6 15.5V12l1.7-4.3A2 2 0 0 1 8.2 6.4h7.6a2 2 0 0 1 1.9 1.3L19.4 12v3.5" />
      <path d="M3.4 12h17.2" />
      <path d="M4.6 15.5h14.8v2.1a.9.9 0 0 1-.9.9h-1.4a.9.9 0 0 1-.9-.9v-.6H7.8v.6a.9.9 0 0 1-.9.9H5.5a.9.9 0 0 1-.9-.9v-2.1Z" />
      <path d="M7 14.2h.01M17 14.2h.01" strokeWidth="2.2" />
    </Svg>
  );
}

/* ── Time & place ─────────────────────────────────────────────────────────── */

export function CalendarIcon(p) {
  return (
    <Svg {...p}>
      <rect x="3.5" y="5.2" width="17" height="15.3" rx="2.4" />
      <path d="M3.5 9.8h17" />
      <path d="M8.2 3.5v3.2M15.8 3.5v3.2" />
      <path d="M7.6 13.4h2M11 13.4h2M14.4 13.4h2M7.6 16.8h2M11 16.8h2" strokeWidth="1.9" />
    </Svg>
  );
}

export function ClockIcon(p) {
  return (
    <Svg {...p}>
      <circle cx="12" cy="12" r="8.4" />
      <path d="M12 7.4V12l3.1 1.9" />
    </Svg>
  );
}

export function LotIcon(p) {
  return (
    <Svg {...p}>
      <rect x="3.6" y="3.6" width="16.8" height="16.8" rx="3.2" />
      <path d="M10 16.4V7.8h2.9a2.7 2.7 0 0 1 0 5.4H10" />
    </Svg>
  );
}

/* ── Money, docs, contact ─────────────────────────────────────────────────── */

export function PriceIcon(p) {
  return (
    <Svg {...p}>
      <path d="M12 3.6v16.8" />
      <path d="M15.8 7.4A3 3 0 0 0 13 5.9h-1.6a2.8 2.8 0 0 0 0 5.6h1.4a2.9 2.9 0 0 1 0 5.8H11a3 3 0 0 1-2.8-1.6" />
    </Svg>
  );
}

export function DocIcon(p) {
  return (
    <Svg {...p}>
      <path d="M13.6 3.6H7a1.9 1.9 0 0 0-1.9 1.9v13a1.9 1.9 0 0 0 1.9 1.9h10a1.9 1.9 0 0 0 1.9-1.9V9.5Z" />
      <path d="M13.6 3.6v5.9h5.3" />
      <path d="M8.6 13.6h6.8M8.6 16.8h4.6" />
    </Svg>
  );
}

export function UploadIcon(p) {
  return (
    <Svg {...p}>
      <path d="M4.6 15.4v2.7a2 2 0 0 0 2 2h10.8a2 2 0 0 0 2-2v-2.7" />
      <path d="M12 15.6V4.2" />
      <path d="M7.9 8.3 12 4.2l4.1 4.1" />
    </Svg>
  );
}

export function MailIcon(p) {
  return (
    <Svg {...p}>
      <rect x="3.4" y="5.4" width="17.2" height="13.2" rx="2.2" />
      <path d="m4.6 7.4 6.3 4.7a2 2 0 0 0 2.2 0l6.3-4.7" />
    </Svg>
  );
}

export function PhoneIcon(p) {
  return (
    <Svg {...p}>
      <path d="M8.1 4.2h-2A1.9 1.9 0 0 0 4.2 6.4c.5 6.9 5.9 12.3 12.8 12.8a1.9 1.9 0 0 0 2.2-1.9v-2a1.5 1.5 0 0 0-1.2-1.5l-2.2-.5a1.5 1.5 0 0 0-1.5.5l-.8 1a11.4 11.4 0 0 1-4.6-4.6l1-.8a1.5 1.5 0 0 0 .5-1.5l-.5-2.2a1.5 1.5 0 0 0-1.5-1.2Z" />
    </Svg>
  );
}

export function PersonIcon(p) {
  return (
    <Svg {...p}>
      <circle cx="12" cy="8.2" r="3.7" />
      <path d="M5 20.2a7 7 0 0 1 14 0" />
    </Svg>
  );
}

/* ── State & actions ──────────────────────────────────────────────────────── */

export function CheckIcon(p) {
  return (
    <Svg {...p}>
      <path d="m5.2 12.6 4.4 4.4 9.2-10" />
    </Svg>
  );
}

// The success animation draws this circle then the tick — see `pc-draw` in the
// stylesheet, which is why the two paths are separate and length-known.
export function CheckCircleIcon({ size = 64, className }) {
  return (
    <svg width={size} height={size} viewBox="0 0 52 52" className={className} aria-hidden="true" focusable="false" {...BASE} strokeWidth="3">
      <circle className="pc-draw-ring" cx="26" cy="26" r="22" />
      <path className="pc-draw-tick" d="m15 27 7.5 7.5L37.5 19" />
    </svg>
  );
}

export function ShieldIcon(p) {
  return (
    <Svg {...p}>
      <path d="M12 3.4 5 6v5.6c0 4.2 2.8 7.9 7 9.1 4.2-1.2 7-4.9 7-9.1V6Z" />
      <path d="m9.2 12.2 2 2 3.6-3.9" />
    </Svg>
  );
}

export function BellIcon(p) {
  return (
    <Svg {...p}>
      <path d="M18.2 16.4H5.8l1.1-1.9V11a5.1 5.1 0 0 1 10.2 0v3.5Z" />
      <path d="M10.2 19.2a2 2 0 0 0 3.6 0" />
    </Svg>
  );
}

// The handshake for "we introduce you" — two arrows meeting a link.
export function ConnectIcon(p) {
  return (
    <Svg {...p}>
      <path d="M9.9 14.1 6.6 17.4a3 3 0 0 1-4.2-4.2L5.6 10" />
      <path d="m14.1 9.9 3.3-3.3a3 3 0 0 1 4.2 4.2L18.4 14" />
      <path d="m9.6 14.4 4.8-4.8" />
    </Svg>
  );
}

export function SearchIcon(p) {
  return (
    <Svg {...p}>
      <circle cx="10.8" cy="10.8" r="6.6" />
      <path d="m15.6 15.6 4.2 4.2" />
    </Svg>
  );
}

export function CloseIcon(p) {
  return (
    <Svg {...p}>
      <path d="m6.4 6.4 11.2 11.2M17.6 6.4 6.4 17.6" />
    </Svg>
  );
}

export function ArrowRightIcon(p) {
  return (
    <Svg {...p}>
      <path d="M4.6 12h14.4" />
      <path d="m14.2 7.2 4.8 4.8-4.8 4.8" />
    </Svg>
  );
}

export function ChevronIcon(p) {
  return (
    <Svg {...p}>
      <path d="m7.6 10 4.4 4.4L16.4 10" />
    </Svg>
  );
}

export function TrashIcon(p) {
  return (
    <Svg {...p}>
      <path d="M4.8 7.4h14.4" />
      <path d="M9.4 7.4V5.6a1.2 1.2 0 0 1 1.2-1.2h2.8a1.2 1.2 0 0 1 1.2 1.2v1.8" />
      <path d="M6.6 7.4l.9 11a1.6 1.6 0 0 0 1.6 1.5h5.8a1.6 1.6 0 0 0 1.6-1.5l.9-11" />
    </Svg>
  );
}

export function SpinnerIcon({ size = 18, className = '' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" className={`pc-spin ${className}`} aria-hidden="true" focusable="false" {...BASE} strokeWidth="2.4">
      <path d="M12 3.6a8.4 8.4 0 1 0 8.4 8.4" />
    </svg>
  );
}
